import React, { useState, useEffect } from 'react';
import { Plus, Search, Filter, Edit, Trash2, FileText, CheckCircle, Clock, AlertTriangle, PauseCircle, Activity, X, Target, Layers, User, Calendar, PieChart, ChevronDown, ChevronsLeft, RefreshCw, Settings, Lock } from 'lucide-react';
import useAppStore from '../stores/useAppStore';
import { api } from '../lib/api';
import useBloqueioScroll from '../hooks/useBloqueioScroll';

// Datas do Protheus vêm como AAAAMMDD (DTOS), sem separador. Guardamos o valor
// cru (não convertido pra Date) — C2_DATPRI/C2_DATPRF não têm hora nenhuma, só
// data, e new Date("AAAA-MM-DD") é interpretado como meia-noite UTC: formatado
// em horário de Brasília (UTC-3) isso vira o dia anterior às 21:00, uma hora que
// nem existe no dado de origem. formatDate() faz o "DD/MM/AAAA" fatiando a
// string direto, sem passar por Date/fuso horário nenhum.

// C2_STATUS não é usado pra exibir status — só vimos o código "N" nos dados reais
// de vocês, sem saber o domínio completo nem o que cada código significa, então
// traduzir isso seria inventar. Em vez disso, calculamos o status igual já
// calculávamos o "progresso": a partir de quantidade produzida vs planejada e do
// prazo (C2_DATPRF) — dado que já confiamos. "Pausada" não entra porque não temos
// nenhum sinal real pra detectar isso (não existe campo de "OP pausada" na SC2
// nem no apontamento) — se aparecer uma fonte real pra isso depois, dá pra somar.
const hojeAAAAMMDD = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}${mm}${dd}`; // mesmo formato de C2_DATPRF, dá pra comparar como string
};

// AAAA-MM-DD (o que <input type="date"> produz sozinho) -> AAAAMMDD (formato
// que o Protheus usa) — troca direta de string, sem passar por Date/fuso
// horário (mesmo cuidado de sempre neste projeto, ver ChaoFabrica.jsx).
const isoParaAaaammdd = (s) => (s ? s.replaceAll('-', '') : '');

// _STATUS_REAL (13/08/2026, pedido do usuário) — vem da ZH6 real (o que
// Iniciar/Pausar/Apontar do operador de fato grava), já agregado no backend
// (ver calcularStatusReal em controllers/protheus.js: Em Produção se QUALQUER
// operação do roteiro estiver assim agora, Pausada se QUALQUER uma estiver
// parada, Concluída só se TODAS estiverem). Tem prioridade sobre o cálculo
// por quantidade/prazo abaixo — só cai nesse cálculo antigo quando a OP nunca
// foi tocada pelo Persona ainda (_STATUS_REAL vem null nesse caso).
const MAPA_STATUS_REAL = {
    'EM PRODUCAO': 'Em Produção',
    'PARADO': 'Pausada',
    'CONCLUIDA': 'Concluída',
    // "EM SETUP" (26/08/2026, pedido do usuário) — não vem da ZH6 como os
    // outros 3 (setup nunca grava lá, de propósito); é o backend
    // (listarOrdens, protheus.js) sobrescrevendo statusPorOperacao com esse
    // valor sintético quando existe um setup ANDAMENTO/PAUSADO pra essa
    // operação agora (ver buscarSetupsAtivosPorOps, setup.js) — sem isso, a
    // operação aparecia "Aguardando" na lista de Operações do Roteiro
    // enquanto a máquina estava preparando ela de verdade.
    'EM SETUP': 'Em Setup',
    // "Em Repreparação" (27/08/2026, pedido do usuário) — mesma origem de
    // "Em Setup" acima (ZS1_TIPO via buscarSetupsAtivosPorOps, setup.js),
    // status próprio pro 2º tipo de setup (refazer depois da 1ª preparação).
    'EM REPREPARACAO': 'Em Repreparação',
};

const calcularStatus = (quantidade, produzido, dataFimPlanejada, hoje, statusReal, dataEncerramento) => {
    // Encerrada é estado da OP inteira e tem precedência sobre qualquer
    // estado calculado das operações. Concluída continua sendo operacional.
    if (String(dataEncerramento || '').trim()) return 'Encerrada';
    if (statusReal && MAPA_STATUS_REAL[statusReal]) return MAPA_STATUS_REAL[statusReal];
    if (quantidade > 0 && produzido >= quantidade) return 'Concluída';
    if (dataFimPlanejada && dataFimPlanejada < hoje) return 'Atrasada';
    if (produzido > 0) return 'Em Produção';
    return 'Aguardando';
};

// Mapeia uma linha crua de SC2 (GET /api/protheus/ordens) pro formato que esta
// tela já usa.
//
// 18/08/2026 (achado do usuário): esta tela chegou a mostrar Máquina/Operador/
// Horário do Apontamento em nível de OP (campos _RECURSO/_OPERADOR/_HORAINI/
// _HORAFIN, calculados pelo backend a partir do apontamento MAIS RECENTE da OP
// inteira) — mas uma OP pode ter várias operações rodando em máquinas e
// operadores diferentes ao mesmo tempo; resumir isso numa única
// máquina/operador (o "mais recente", que pode nem estar mais ativo) é
// enganoso. Removido daqui — a seção "Operações do Roteiro" do Modal Detalhe
// já mostra a máquina certa, POR operação, sem inventar um resumo da OP
// inteira.
const normalizarOrdemProtheus = (row, hoje) => {
    const quantidade = Number(row.C2_QUANT) || 0;
    const produzido = Number(row.C2_QUJE) || 0;
    const refugado = Number(row._REFUGADO) || 0;
    // Código completo da OP = C2_NUM + C2_ITEM + C2_SEQUEN (11 caracteres) — é o
    // mesmo valor de H6_OP, não só o número "visível" de 6 dígitos (ver
    // observação em protheusFieldMap.js). Usar só C2_NUM cortava a chave e podia
    // colidir duas OPs com o mesmo número em itens/sequências diferentes.
    const codigoCompleto = `${row.C2_NUM}${row.C2_ITEM}${row.C2_SEQUEN}`.trim();
    return {
        id: codigoCompleto,
        codigo: codigoCompleto,
        produto: row.C2_PRODUTO,
        produtoNome: (row._PRODUTO_NOME || '').trim() || null,
        // roteiro (13/08/2026) — precisa pro Modal Detalhe buscar as
        // operações do roteiro sob demanda (GET /api/protheus/roteiro).
        roteiro: (row.C2_ROTEIRO || '').trim() || null,
        quantidade,
        unidade: (row.C2_UM || 'un').trim(),
        produzido,
        refugado,
        data_inicio: row.C2_DATPRI,
        previsao_fim: row.C2_DATPRF,
        dataEncerramento: String(row.C2_DATRF || '').trim() || null,
        prioridade: row.C2_PRIOR || '—',
        status: calcularStatus(quantidade, produzido, row.C2_DATPRF, hoje, row._STATUS_REAL, row.C2_DATRF),
        progresso: quantidade > 0 ? Math.min(Math.round((produzido / quantidade) * 100), 100) : 0,
        // Status de cada operação do roteiro (mapa { operacao: status ZH6 })
        // — usado só pelo Modal Detalhe pra mostrar a lista por operação,
        // já veio pronto do backend junto com o resto da linha.
        statusPorOperacao: row._STATUS_POR_OPERACAO || {},
        // Sessão por operação (18/08/2026, pedido do usuário: mostrar
        // operador/horário de CADA operação, não da OP inteira — mesmo
        // motivo do fix anterior, só que aqui a informação É confiável
        // porque está escopada a uma única operação específica).
        sessaoPorOperacao: row._SESSAO_POR_OPERACAO || {},
        // Meta ajustada da 1ª operação (18/08/2026, pedido do usuário) —
        // mesma regra já usada na tela do operador (Producao.jsx/"Reaberta"):
        // refugo registrado numa operação POSTERIOR à primeira soma na meta
        // dela. null quando o roteiro ainda não foi resolvido; quando existe,
        // `meta === quantidade` é o caso normal (sem refugo posterior nenhum).
        metaAjustadaPrimeiraOperacao: row._META_AJUSTADA_PRIMEIRA_OPERACAO || null,
        // Meta de CADA operação (19/08/2026, achado real do usuário: uma
        // operação sem ser a 1ª pode receber mais peças da anterior do que a
        // quantidade flat — a meta dela também precisa refletir isso, senão
        // Saldo mostra menos do que realmente dá pra produzir).
        metaPorOperacao: row._META_POR_OPERACAO || {},
        // Produzido de cada operação (14/08/2026, pedido do usuário: barra
        // fina de progresso por operação no Modal Detalhe, "só pra termos a
        // noção") — ZH6+SH6 já combinados no backend, mesma regra de nunca
        // somar entre operações diferentes.
        produzidoPorOperacao: row._PRODUZIDO_POR_OPERACAO || {},
        // Refugado de cada operação (17/08/2026, pedido do usuário: detalhe
        // completo ao clicar numa operação) — mesma origem/regra do produzido
        // por operação acima (ZH6+SH6 combinados no backend, nunca somado
        // entre operações diferentes).
        refugadoPorOperacao: row._REFUGADO_POR_OPERACAO || {},
    };
};

const statusBadgeClass = {
    'Encerrada': 'mes-status--muted',
    'Em Produção': 'mes-status--active',
    'Concluída': 'mes-status--success',
    'Aguardando': 'mes-status--muted',
    'Atrasada': 'mes-status--danger',
    'Pausada': 'mes-status--warning',
    // "Produzido Parcial" (14/08/2026, pedido do usuário) — ZH6_STATUS de uma
    // operação sempre volta pra AGUARDANDO entre ciclos (cada apontamento é
    // um registro fechado, ver criarApontamento), mesmo quando já tem
    // produção real acumulada ali. "Aguardando" sozinho não distinguia "nunca
    // foi tocada" de "já produziu algo, só não está ativa agora" — usa
    // warning (amber) pra marcar visualmente que já teve trabalho de
    // verdade, sem ser confundido com "em produção agora" (azul).
    'Produzido Parcial': 'mes-status--warning',
    // "Reaberta" (19/08/2026, padronizado com a tela do operador, que já
    // tinha esse status) — reaproveita a mesma classe/cor "roxo" (19/08/2026,
    // achado do usuário: violeta cru do Tailwind destoava do resto da
    // paleta, mais escura industrial — ver token `roxo` em tailwind.config.js).
    'Reaberta': 'mes-status--purple',
    // "Em Setup" (26/08/2026) — mesma cor roxa já usada pra Setup em todo o
    // resto do sistema (Producao.jsx/ChaoFabrica.jsx).
    'Em Setup': 'mes-status--purple',
    // "Em Repreparação" (27/08/2026) — indigo, mesma cor nova usada em
    // Producao.jsx/ChaoFabrica.jsx pra manter a paleta consistente.
    'Em Repreparação': 'mes-status--indigo',
};

const statusIcon = {
    'Encerrada': <Lock size={13} />,
    'Em Produção': <Activity size={13} />,
    'Concluída': <CheckCircle size={13} />,
    'Aguardando': <Clock size={13} />,
    'Atrasada': <AlertTriangle size={13} />,
    'Pausada': <PauseCircle size={13} />,
    'Produzido Parcial': <PieChart size={13} />,
    'Reaberta': <RefreshCw size={13} />,
    'Em Setup': <Settings size={13} />,
    'Em Repreparação': <RefreshCw size={13} />,
};

const progressFillClass = {
    'Encerrada': 'mes-progress-fill--petrol',
    'Em Produção': 'mes-progress-fill--brand',
    'Concluída': 'mes-progress-fill--petrol',
    'Aguardando': 'mes-progress-fill--brand',
    'Atrasada': 'mes-progress-fill--red',
    'Pausada': 'mes-progress-fill--amber',
    'Produzido Parcial': 'mes-progress-fill--amber',
    'Reaberta': 'mes-progress-fill--purple',
    'Em Setup': 'mes-progress-fill--purple',
    'Em Repreparação': 'mes-progress-fill--indigo',
};

const OrdensProducao = () => {
    const { currentClient } = useAppStore();
    const [ordens, setOrdens] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [erroApi, setErroApi] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    // Busca no servidor, não mais um .filter() em cima das linhas já
    // carregadas (27/08/2026, pedido do usuário: "abranger todas as OPs,
    // conforme ele pesquisar, faz a query no banco") — searchTermAplicado é
    // a versão "assentada" (debounced) de searchTerm, só atualizada 400ms
    // depois que o usuário parar de digitar, pra não disparar 1 consulta por
    // tecla. O useEffect de busca (mais abaixo) depende dela, não de
    // searchTerm direto.
    const [searchTermAplicado, setSearchTermAplicado] = useState('');
    // Quantidade de OPs por página (27/08/2026, pedido do usuário) — 50 era
    // fixo no código antes; agora é escolhido pelo usuário (50/75/100).
    const [quantidadeSelecionada, setQuantidadeSelecionada] = useState(50);
    // Filtro de período (27/08/2026, pedido do usuário) — sobre C2_DATPRI
    // (Data de Emissão/Início Previsto), mesmo campo que já ordena a lista
    // hoje. Guardado em ISO ("AAAA-MM-DD", o que <input type="date"> produz
    // sozinho) — convertido pra AAAAMMDD só na hora de montar a query,
    // mesmo cuidado de sempre neste projeto (nunca passar por Date/fuso).
    const [dataInicioFiltro, setDataInicioFiltro] = useState('');
    const [dataFimFiltro, setDataFimFiltro] = useState('');
    const [statusFilter, setStatusFilter] = useState('Todos');

    // Paginação por BLOCOS (27/08/2026) — substitui de vez a 1ª versão desta
    // feature, que usava `COUNT(*) OVER()` pra saber o total exato de
    // registros e travava a consulta em ~32s (achado real do usuário, via
    // log: contar TODAS as OPs que batem com o filtro obriga a varrer o
    // histórico inteiro da SC2, 20+ anos). Trocado por: busca um BLOCO de
    // `PAGINAS_POR_BLOCO` páginas de uma vez (4x a quantidade escolhida —
    // 200/300/400 pra 50/75/100) e só consulta o servidor de novo quando o
    // usuário navega além do bloco carregado; dentro do bloco, trocar de
    // página é só reposicionar uma fatia em memória (ver ordensPaginadas
    // mais abaixo), sem gastar consulta nova. Isso também substitui por
    // completo o antigo "Lote" (que só existia com Status ativo) — agora os
    // 2 modos (Status "Todos" ou ativo) usam o MESMO mecanismo, sem
    // distinção nenhuma; o Status continua sendo filtrado em JS em cima do
    // que já foi carregado, do jeito que já era.
    const PAGINAS_POR_BLOCO = 4;
    const [blocoAtual, setBlocoAtual] = useState(1);
    const [paginaNoBloco, setPaginaNoBloco] = useState(1);
    // "Existe bloco seguinte?" sem contar nada — o backend pede 1 linha a
    // mais que o bloco e avisa se ela veio (ver controllers/protheus.js).
    // Sem isso não teríamos como saber se o botão Próxima deve continuar
    // habilitado ao chegar no fim do bloco carregado. Não existe mais
    // "Página X de Y" exato — decisão confirmada com o usuário, pra não
    // reintroduzir a mesma lentidão.
    const [temMaisBlocos, setTemMaisBlocos] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => {
            setSearchTermAplicado(searchTerm.trim());
            setBlocoAtual(1);
            setPaginaNoBloco(1);
        }, 400);
        return () => clearTimeout(t);
    }, [searchTerm]);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [opDetalhe, setOpDetalhe] = useState(null);
    useBloqueioScroll(isModalOpen || !!opDetalhe);
    // Operações do roteiro da OP aberta no Modal Detalhe (13/08/2026) —
    // buscadas sob demanda só quando o modal abre (mesmo padrão de
    // carregandoOperacoes em Producao.jsx), não pras 50 linhas da lista de
    // uma vez. O status de cada operação já vem pronto em
    // opDetalhe.statusPorOperacao (o backend já mandou junto com a linha).
    const [operacoesRoteiroDetalhe, setOperacoesRoteiroDetalhe] = useState([]);
    const [carregandoOperacoesDetalhe, setCarregandoOperacoesDetalhe] = useState(false);
    // Operação expandida dentro do Modal Detalhe (17/08/2026, pedido do
    // usuário: clicar numa operação mostra quantidade/produzido/saldo/
    // refugo/máquina daquela operação especificamente) — null = nenhuma
    // aberta, string = código da operação (G2_OPERAC) expandida agora.
    const [operacaoExpandida, setOperacaoExpandida] = useState(null);
    const [novaOp, setNovaOp] = useState({ produto: '', quantidade: '', unidade: 'un', maquina: 'Extrusora Alpha-1', data_inicio: '', prioridade: 'Normal' });

    useEffect(() => {
        setOperacaoExpandida(null);
        if (!opDetalhe?.produto || !opDetalhe?.roteiro) { setOperacoesRoteiroDetalhe([]); return; }
        setCarregandoOperacoesDetalhe(true);
        api.get(`/api/protheus/roteiro?produto=${encodeURIComponent(opDetalhe.produto)}&roteiro=${encodeURIComponent(opDetalhe.roteiro)}`)
            .then((operacoes) => setOperacoesRoteiroDetalhe([...operacoes].sort((a, b) => Number(a.G2_OPERAC) - Number(b.G2_OPERAC))))
            .catch(() => setOperacoesRoteiroDetalhe([]))
            .finally(() => setCarregandoOperacoesDetalhe(false));
    }, [opDetalhe?.codigo]);

    // Dados reais, buscados ao vivo via SQL no banco do Protheus (ver backend/src/routes/protheus.js).
    // "Nova OP" / editar / cancelar abaixo continuam só mexendo no estado local desta tela —
    // não gravam no Protheus (isso depende de ExecAuto, ainda não implementado).
    //
    // Note que `statusFilter` NÃO entra nos parâmetros da consulta nem no
    // tamanho do bloco — o Status nunca foi filtrável no SQL (é calculado
    // depois, ver comentário mais abaixo em `filteredOrdens`), e agora o
    // tamanho do bloco também não depende mais dele (antes dependia, quando
    // ainda existia o "Lote" separado). Trocar o Status só reorganiza em
    // memória o que já está carregado — só entra nos handlers de reset
    // (mais abaixo) porque um Status novo pode ter poucos/nenhum resultado
    // na página atual, mesma razão de sempre.
    useEffect(() => {
        const tamanhoBloco = quantidadeSelecionada * PAGINAS_POR_BLOCO;
        const buscarOrdens = () => {
            const params = new URLSearchParams({ limit: tamanhoBloco, pagina: blocoAtual });
            if (searchTermAplicado) params.set('busca', searchTermAplicado);
            if (dataInicioFiltro) params.set('dataInicio', isoParaAaaammdd(dataInicioFiltro));
            if (dataFimFiltro) params.set('dataFim', isoParaAaaammdd(dataFimFiltro));
            return api.get(`/api/protheus/ordens?${params.toString()}`)
                .then(({ ordens: ordensRows, temMais }) => {
                    const hoje = hojeAAAAMMDD();
                    setOrdens(ordensRows.map((row) => normalizarOrdemProtheus(row, hoje)));
                    setTemMaisBlocos(!!temMais);
                })
                .catch((err) => setErroApi(err.message || 'Erro ao carregar ordens de produção.'));
        };

        setCarregando(true);
        buscarOrdens().finally(() => setCarregando(false));
        // Atualização automática (13/08/2026, pedido do usuário: status real
        // — ver _STATUS_REAL/calcularStatus abaixo — precisa aparecer sozinho
        // na tela, sem precisar recarregar). Sem WebSocket/SSE neste projeto
        // hoje, a saída é buscar de novo em intervalos curtos. 15s: rápido o
        // bastante pra parecer "ao vivo" numa tela de gestão, sem virar
        // bombardeio de requisição. Reiniciado a cada mudança de
        // busca/filtro/quantidade/bloco (o efeito inteiro re-executa), pra
        // sempre manter os MESMOS parâmetros vivos — nunca um refresh
        // silencioso "esquecendo" o filtro/bloco que o usuário está vendo.
        const intervalo = setInterval(buscarOrdens, 15000);
        return () => clearInterval(intervalo);
    }, [quantidadeSelecionada, dataInicioFiltro, dataFimFiltro, searchTermAplicado, blocoAtual]);

    // Só o Status continua sendo filtrado aqui (client-side) — texto e
    // período já vêm filtrados do servidor (ver useEffect acima).
    const filteredOrdens = ordens.filter(ordem =>
        statusFilter === 'Todos' || ordem.status === statusFilter
    );

    // Fatia de exibição, em memória, dentro do bloco carregado — o mesmo
    // princípio vale nos 2 modos (Status "Todos" ou ativo) agora.
    const ordensPaginadas = filteredOrdens.slice(
        (paginaNoBloco - 1) * quantidadeSelecionada,
        paginaNoBloco * quantidadeSelecionada
    );
    // Número de página ABSOLUTO (contínuo, cruzando blocos) — só cosmético,
    // pro usuário ver "Página 5" em vez de "Bloco 2, página 1".
    const paginaAbsoluta = (blocoAtual - 1) * PAGINAS_POR_BLOCO + paginaNoBloco;
    // Tem mais uma fatia dentro do PRÓPRIO bloco já carregado?
    const temProximaLocal = paginaNoBloco * quantidadeSelecionada < filteredOrdens.length;
    // Pode avançar: ainda tem fatia local, OU está na última fatia local do
    // bloco e existe bloco seguinte no servidor (ver temMaisBlocos).
    const podeAvancar = temProximaLocal || (paginaNoBloco >= PAGINAS_POR_BLOCO && temMaisBlocos);
    // Pode voltar: tem fatia local anterior, OU existe bloco anterior.
    const podeVoltar = paginaNoBloco > 1 || blocoAtual > 1;

    // Volta o scroll pro topo a cada troca de página (27/08/2026, pedido do
    // usuário: "quando trocar de página, deveria mudar a tela para o topo,
    // hoje ela fica embaixo") — o botão fica no rodapé da tabela, então sem
    // isso o usuário trocava de página e continuava olhando pro rodapé, com
    // a tabela nova começando lá em cima, fora da vista. Esta tela não tem
    // container de rolagem próprio (ver MainLayout.jsx — o <main> rola
    // junto com a janela), por isso `window.scrollTo` já é suficiente.
    const voltarAoTopo = () => window.scrollTo({ top: 0, behavior: 'smooth' });

    const handleProximaPagina = () => {
        if (temProximaLocal) { setPaginaNoBloco((p) => p + 1); voltarAoTopo(); return; }
        if (temMaisBlocos) { setBlocoAtual((b) => b + 1); setPaginaNoBloco(1); voltarAoTopo(); }
    };
    const handleAnteriorPagina = () => {
        if (paginaNoBloco > 1) { setPaginaNoBloco((p) => p - 1); voltarAoTopo(); return; }
        if (blocoAtual > 1) { setBlocoAtual((b) => b - 1); setPaginaNoBloco(1); voltarAoTopo(); }
    };
    // Botão "voltar pro início" (27/08/2026, pedido do usuário) — pula
    // direto pra página 1 do bloco 1, de qualquer ponto da navegação.
    const handlePrimeiraPagina = () => { setBlocoAtual(1); setPaginaNoBloco(1); voltarAoTopo(); };

    // Segurança (27/08/2026) — a atualização automática de 15s pode encolher
    // o que bate com o Status dentro do bloco atual enquanto o usuário está
    // numa página local que deixou de existir; sem isso, a tela mostrava
    // "Nenhuma ordem encontrada" mesmo tendo dado nas páginas anteriores do
    // mesmo bloco.
    useEffect(() => {
        const totalPaginasLocais = Math.max(1, Math.ceil(filteredOrdens.length / quantidadeSelecionada));
        if (paginaNoBloco > totalPaginasLocais) setPaginaNoBloco(totalPaginasLocais);
    }, [filteredOrdens.length, quantidadeSelecionada]);

    // Opções do filtro vêm dos valores reais de C2_STATUS presentes nos dados —
    // não tem tradução pro rótulo em português definida (ver protheusFieldMap.js),
    // então mostramos o código bruto como veio do Protheus.
    const statusOptions = [...new Set(ordens.map(o => o.status))].sort();

    // Cada troca de filtro/quantidade volta pro bloco 1, página 1 (27/08/2026)
    // — senão dava pra ficar "preso" num bloco/página de um filtro novo que
    // não tem tanta coisa assim, mostrando uma lista vazia sem explicação.
    // Resetar dentro do MESMO handler (não num useEffect à parte) garante
    // que os estados mudam juntos, numa única leva.
    const handleMudarQuantidade = (q) => { setQuantidadeSelecionada(q); setBlocoAtual(1); setPaginaNoBloco(1); };
    const handleMudarStatus = (s) => { setStatusFilter(s); setBlocoAtual(1); setPaginaNoBloco(1); };
    const handleMudarDataInicio = (v) => { setDataInicioFiltro(v); setBlocoAtual(1); setPaginaNoBloco(1); };
    const handleMudarDataFim = (v) => { setDataFimFiltro(v); setBlocoAtual(1); setPaginaNoBloco(1); };
    const handleLimparPeriodo = () => { setDataInicioFiltro(''); setDataFimFiltro(''); setBlocoAtual(1); setPaginaNoBloco(1); };

    const formatDate = (dateStr) => {
        if (!dateStr) return '--';
        const s = String(dateStr).trim();
        if (/^\d{8}$/.test(s)) return `${s.slice(6, 8)}/${s.slice(4, 6)}/${s.slice(0, 4)}`;
        return new Date(dateStr).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    };

    const handleSalvarOp = () => {
        if (!novaOp.produto) return alert('O nome do produto é obrigatório.');
        const nextId = ordens.length > 0 ? Math.max(...ordens.map(o => o.id)) + 1 : 1;
        const newCode = `OP-2026-${String(nextId).padStart(3, '0')}`;
        const ordemParaAdicionar = { id: nextId, codigo: newCode, produto: novaOp.produto, quantidade: Number(novaOp.quantidade) || 1, maquina: novaOp.maquina, operador: 'Não Atribuído', data_inicio: novaOp.data_inicio || new Date().toISOString().slice(0, 16), previsao_fim: novaOp.data_inicio || new Date().toISOString().slice(0, 16), prioridade: novaOp.prioridade, status: 'Aguardando', progresso: 0 };
        setOrdens([ordemParaAdicionar, ...ordens]);
        setIsModalOpen(false);
        setNovaOp({ produto: '', quantidade: '', unidade: 'un', maquina: 'Extrusora Alpha-1', data_inicio: '', prioridade: 'Normal' });
    };

    const handleRemoverOp = (id) => {
        if (window.confirm('Deseja realmente remover esta Ordem de Produção?')) {
            setOrdens(ordens.filter(o => o.id !== id));
        }
    };

    return (
        <div className="h-full flex flex-col">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 space-y-4 md:space-y-0">
                <div>
                    <h1 className="text-2xl font-bold text-anthracite-50 tracking-tight flex items-center gap-2.5">
                        <FileText size={22} className="text-brand-400" /> Ordens de Produção
                    </h1>
                    <p className="text-[14px] text-anthracite-500 mt-1">Gerenciamento completo do fluxo de OPs</p>
                </div>
                <button onClick={() => setIsModalOpen(true)} className="mes-btn-primary">
                    <Plus size={16} /> Nova OP
                </button>
            </div>

            {/* Filtros */}
            <div className="mes-card-elevated p-5 mb-6 flex flex-col gap-4">
                <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                    <div className="relative w-full md:w-96">
                        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-anthracite-500" />
                        <input type="text" className="mes-input pl-11" placeholder="Buscar por código ou produto..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    </div>
                    <div className="flex items-center gap-3">
                        <Filter size={14} className="text-anthracite-500" />
                        <select className="mes-input w-auto" value={statusFilter} onChange={(e) => handleMudarStatus(e.target.value)}>
                            <option value="Todos">Todos os Status</option>
                            {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                </div>
                <div className="flex flex-col md:flex-row gap-4 items-center justify-between border-t border-white/[0.04] pt-4">
                    <div className="flex items-center gap-3">
                        <Calendar size={14} className="text-anthracite-500 shrink-0" />
                        <input type="date" className="mes-input w-auto" value={dataInicioFiltro} onChange={(e) => handleMudarDataInicio(e.target.value)} title="Data de início (Emissão/Início Previsto)" />
                        <span className="text-anthracite-500 text-[12px]">até</span>
                        <input type="date" className="mes-input w-auto" value={dataFimFiltro} onChange={(e) => handleMudarDataFim(e.target.value)} title="Data de fim (Emissão/Início Previsto)" />
                        {(dataInicioFiltro || dataFimFiltro) && (
                            <button onClick={handleLimparPeriodo} className="text-[12px] font-medium text-anthracite-500 hover:text-anthracite-300">Limpar período</button>
                        )}
                    </div>
                    {/* Quantidade (27/08/2026, pedido do usuário) — antes fixo em
                        50 no código; agora também define o tamanho do bloco
                        buscado de uma vez (4x este valor, ver PAGINAS_POR_BLOCO). */}
                    <div className="flex items-center gap-2">
                        <span className="text-[12px] text-anthracite-500">Mostrar</span>
                        <div className="flex gap-1 bg-surface-3/50 rounded-xl p-1">
                            {[50, 75, 100].map((q) => (
                                <button key={q} onClick={() => handleMudarQuantidade(q)}
                                    className={`px-3 py-1.5 rounded-lg text-[12px] font-medium transition-colors ${quantidadeSelecionada === q ? 'bg-surface-2 text-brand-400 shadow-sm' : 'text-anthracite-500 hover:text-anthracite-300'}`}>
                                    {q}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabela */}
            <div className="mes-card flex-1 overflow-hidden flex flex-col">
                <div className="overflow-x-auto">
                    <table className="mes-table">
                        <thead>
                            <tr>
                                <th>Código / Produto</th>
                                <th>Status / Progresso</th>
                                <th>Planejamento</th>
                                <th className="text-right">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {carregando ? (
                                <tr><td colSpan="4" className="text-center py-12 text-anthracite-500">Carregando ordens de produção...</td></tr>
                            ) : erroApi ? (
                                <tr><td colSpan="4" className="text-center py-12 text-red-400">{erroApi}</td></tr>
                            ) : ordensPaginadas.length > 0 ? ordensPaginadas.map((ordem) => (
                                <tr key={ordem.id} onClick={() => setOpDetalhe(ordem)} className="cursor-pointer">
                                    <td>
                                        <div className="text-[13px] font-bold text-anthracite-100">{ordem.codigo}</div>
                                        <div className="text-[12px] text-anthracite-500 mt-0.5">{ordem.produtoNome || ordem.produto} · {ordem.quantidade.toLocaleString('pt-BR')} {ordem.unidade || 'un'}</div>
                                    </td>
                                    <td>
                                        <span className={`mes-status ${statusBadgeClass[ordem.status] || 'mes-status--muted'}`}>
                                            {statusIcon[ordem.status]}{ordem.status}
                                        </span>
                                        <div className="mt-2.5 w-32">
                                            <div className="flex justify-between text-[10px] mb-1.5">
                                                <span className="text-anthracite-500">{ordem.progresso}%</span>
                                            </div>
                                            <div className="mes-progress-track mes-progress-sm">
                                                <div className={`mes-progress-fill ${progressFillClass[ordem.status] || 'mes-progress-fill--brand'}`} style={{ width: `${ordem.progresso}%` }} />
                                            </div>
                                        </div>
                                    </td>
                                    <td className="text-[12px] text-anthracite-500">
                                        <div><span className="font-medium text-anthracite-400">Início:</span> {formatDate(ordem.data_inicio)}</div>
                                        <div className="mt-0.5"><span className="font-medium text-anthracite-400">Fim:</span> {formatDate(ordem.previsao_fim)}</div>
                                    </td>
                                    <td className="text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            <button onClick={(e) => e.stopPropagation()} className="p-2 rounded-xl hover:bg-white/[0.04] text-anthracite-500 hover:text-amber-400 transition-colors" title="Editar"><Edit size={15} /></button>
                                            <button onClick={(e) => { e.stopPropagation(); handleRemoverOp(ordem.id); }} className="p-2 rounded-xl hover:bg-white/[0.04] text-anthracite-500 hover:text-red-400 transition-colors" title="Cancelar"><Trash2 size={15} /></button>
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan="4" className="text-center py-12 text-anthracite-500">Nenhuma ordem de produção encontrada.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="px-6 py-4 border-t border-white/[0.04] flex items-center justify-between mt-auto flex-wrap gap-3">
                    {/* Paginação por blocos (27/08/2026) — ver comentário
                        completo na declaração de PAGINAS_POR_BLOCO. Sem
                        "Página X de Y" exato de propósito (custaria caro
                        demais pra calcular, ver bug real corrigido) — só
                        "Página N" com Anterior/Próxima habilitados conforme
                        existe mais dado pra trás/pra frente. */}
                    <div className="text-[12px] text-anthracite-500">
                        Mostrando <span className="font-medium text-anthracite-300">{ordensPaginadas.length}</span> resultados
                    </div>
                    <div className="flex items-center gap-3">
                        {/* Voltar ao início (27/08/2026, pedido do usuário) —
                            pula direto pra página 1, de qualquer ponto. */}
                        <button onClick={handlePrimeiraPagina} disabled={paginaAbsoluta === 1} title="Voltar para a página inicial"
                            className="mes-btn-ghost p-1.5 disabled:opacity-40 disabled:cursor-not-allowed">
                            <ChevronsLeft size={16} />
                        </button>
                        <button onClick={handleAnteriorPagina} disabled={!podeVoltar}
                            className="mes-btn-ghost px-3 py-1.5 text-[12px] disabled:opacity-40 disabled:cursor-not-allowed">
                            Anterior
                        </button>
                        <span className="text-[12px] text-anthracite-500">Página <span className="font-medium text-anthracite-300">{paginaAbsoluta}</span></span>
                        <button onClick={handleProximaPagina} disabled={!podeAvancar}
                            className="mes-btn-ghost px-3 py-1.5 text-[12px] disabled:opacity-40 disabled:cursor-not-allowed">
                            Próxima
                        </button>
                    </div>
                </div>
            </div>

            {/* Modal Nova OP */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
                    <div className="mes-modal w-full max-w-lg p-7 animate-scale-in">
                        <div className="flex items-center justify-between mb-6 pb-5 border-b border-white/[0.04]">
                            <h3 className="text-lg font-bold text-anthracite-100">Nova Ordem de Produção</h3>
                            <button onClick={() => setIsModalOpen(false)} className="p-2 rounded-xl hover:bg-white/[0.04] text-anthracite-500"><X size={16} /></button>
                        </div>
                        <div className="space-y-5">
                            <div>
                                <label className="block text-[11px] font-bold text-anthracite-400 uppercase tracking-[0.15em] mb-2">Produto / SKU</label>
                                <input type="text" className="mes-input" placeholder="Ex: Bobina Filme Stretch" value={novaOp.produto} onChange={(e) => setNovaOp({ ...novaOp, produto: e.target.value })} />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-[11px] font-bold text-anthracite-400 uppercase tracking-[0.15em] mb-2">Quantidade</label>
                                    <input type="number" className="mes-input" placeholder="0" value={novaOp.quantidade} onChange={(e) => setNovaOp({ ...novaOp, quantidade: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-[11px] font-bold text-anthracite-400 uppercase tracking-[0.15em] mb-2">Unidade</label>
                                    <select className="mes-input" value={novaOp.unidade} onChange={(e) => setNovaOp({ ...novaOp, unidade: e.target.value })}>
                                        <option value="kg">kg</option><option value="un">un</option><option value="litros">litros</option><option value="metros">metros</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-[11px] font-bold text-anthracite-400 uppercase tracking-[0.15em] mb-2">Equipamento</label>
                                <select className="mes-input" value={novaOp.maquina} onChange={(e) => setNovaOp({ ...novaOp, maquina: e.target.value })}>
                                    <option>Extrusora Alpha-1</option><option>Injetora B-200</option><option>Misturador M3</option><option>Embaladora P-2</option>
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-[11px] font-bold text-anthracite-400 uppercase tracking-[0.15em] mb-2">Início Previsto</label>
                                    <input type="datetime-local" className="mes-input" value={novaOp.data_inicio} onChange={(e) => setNovaOp({ ...novaOp, data_inicio: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-[11px] font-bold text-anthracite-400 uppercase tracking-[0.15em] mb-2">Prioridade</label>
                                    <select className="mes-input" value={novaOp.prioridade} onChange={(e) => setNovaOp({ ...novaOp, prioridade: e.target.value })}>
                                        <option>Baixa</option><option>Normal</option><option>Média</option><option>Alta</option><option>Urgente</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 mt-7 pt-5 border-t border-white/[0.04]">
                            <button onClick={() => setIsModalOpen(false)} className="mes-btn-ghost">Cancelar</button>
                            <button onClick={handleSalvarOp} className="mes-btn-primary">Salvar Ordem</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Detalhe */}
            {opDetalhe && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
                    {/* max-h-[85vh] + flex flex-col (13/08/2026) — sem isso, a seção
                        nova de "Operações do Roteiro" (pode ter 10+ linhas) empurrava
                        o modal pra fora da tela, sem scroll nenhum (achado real do
                        usuário, roteiro de 11 operações). Mesmo padrão já usado em
                        ModalHistorico logo abaixo neste arquivo: cabeçalho e rodapé
                        fixos, só o meio rola. */}
                    <div className="mes-modal w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden animate-scale-in">
                        <div className="px-7 py-5 border-b border-white/[0.04] flex items-start justify-between shrink-0">
                            <div>
                                <h3 className="text-lg font-bold text-anthracite-100 flex items-center gap-2"><FileText size={18} className="text-brand-400" /> {opDetalhe.codigo}</h3>
                                <p className="text-[12px] text-anthracite-500 mt-0.5">{opDetalhe.produtoNome || opDetalhe.produto}</p>
                            </div>
                            <button onClick={() => setOpDetalhe(null)} className="p-2 rounded-xl hover:bg-white/[0.04] text-anthracite-500"><X size={16} /></button>
                        </div>
                        <div className="p-7 space-y-6 flex-1 overflow-y-auto">
                            {/* Produzido "real" (14/08/2026, achado do usuário): opDetalhe.produzido
                                vem de C2_QUJE (Protheus), que nunca atualiza desde que o ExecAuto foi
                                desativado — fica travado (geralmente 0) mesmo com produção real
                                registrada via ZH6/SH6. A ÚLTIMA operação do roteiro é quem reflete
                                quantas peças passaram por TODAS as etapas de verdade até o fim (nunca
                                soma entre operações — mesma regra de sempre, ver produzidoOperacao mais
                                abaixo). Sem roteiro carregado ainda, cai no valor antigo (resiliência
                                de sempre) — some pro valor certo assim que o roteiro chega. */}
                            {(() => {
                                const ultimaOperacao = operacoesRoteiroDetalhe[operacoesRoteiroDetalhe.length - 1];
                                const produzidoReal = ultimaOperacao
                                    ? (opDetalhe.produzidoPorOperacao?.[(ultimaOperacao.G2_OPERAC || '').trim()] || 0)
                                    : (opDetalhe.produzido ?? 0);
                                const progressoReal = opDetalhe.quantidade > 0
                                    ? Math.min(Math.round((produzidoReal / opDetalhe.quantidade) * 100), 100)
                                    : 0;
                                return (
                                    <>
                                        <div className="flex items-center justify-between">
                                            <span className={`mes-status ${statusBadgeClass[opDetalhe.status] || 'mes-status--muted'}`}>
                                                {statusIcon[opDetalhe.status]}{opDetalhe.status}
                                            </span>
                                            <span className="text-sm font-bold text-brand-400">{progressoReal}%</span>
                                        </div>
                                        <div className="mes-progress-track">
                                            <div className={`mes-progress-fill ${progressFillClass[opDetalhe.status] || 'mes-progress-fill--brand'}`} style={{ width: `${progressoReal}%` }} />
                                        </div>
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                            {[
                                                { label: 'Quantidade', value: opDetalhe.quantidade.toLocaleString('pt-BR'), icon: Target, color: 'text-anthracite-300' },
                                                { label: 'Produzido', value: produzidoReal.toLocaleString('pt-BR'), icon: CheckCircle, color: 'text-petrol-400' },
                                                { label: 'Saldo', value: Math.max(opDetalhe.quantidade - produzidoReal, 0).toLocaleString('pt-BR'), icon: Layers, color: 'text-brand-400' },
                                                { label: 'Refugado', value: (opDetalhe.refugado ?? 0).toLocaleString('pt-BR'), icon: AlertTriangle, color: 'text-red-400' },
                                            ].map((item, i) => (
                                                <div key={i} className="mes-card-sunken p-4 text-center">
                                                    <item.icon size={16} className={`mx-auto mb-1.5 ${item.color}`} />
                                                    <span className="block text-[10px] text-anthracite-500 mb-1">{item.label}</span>
                                                    <span className={`text-lg font-bold ${item.color}`}>{item.value}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                );
                            })()}
                            {/* Operações do roteiro (13/08/2026) — status individual de cada
                                etapa, já que ZH6_STATUS é gravado por operação, não pela OP
                                inteira (ver calcularStatusReal em controllers/protheus.js). */}
                            {(carregandoOperacoesDetalhe || operacoesRoteiroDetalhe.length > 0) && (
                                <div>
                                    <span className="block text-[10px] text-anthracite-500 uppercase mb-2">Operações do Roteiro</span>
                                    {carregandoOperacoesDetalhe ? (
                                        <p className="text-[12px] text-anthracite-500">Carregando...</p>
                                    ) : (
                                        <div className="space-y-1.5">
                                            {operacoesRoteiroDetalhe.map((seq, indiceOperacao) => {
                                                const operac = (seq.G2_OPERAC || '').trim();
                                                const statusZH6 = opDetalhe.statusPorOperacao?.[operac];
                                                // Barra fina de progresso (14/08/2026, pedido do usuário: "só
                                                // pra termos a noção") — produzido DESSA operação contra a
                                                // quantidade planejada da OP (o teto é o mesmo pra qualquer
                                                // operação, o lote inteiro passa por cada etapa).
                                                const produzidoOperacao = opDetalhe.produzidoPorOperacao?.[operac] || 0;
                                                const progressoOperacao = opDetalhe.quantidade > 0
                                                    ? Math.min((produzidoOperacao / opDetalhe.quantidade) * 100, 100)
                                                    : 0;
                                                const statusBase = MAPA_STATUS_REAL[statusZH6] || 'Aguardando';
                                                // Meta ajustada (18/08/2026, pedido do usuário) — só a 1ª operação
                                                // do roteiro pode ter meta maior que a quantidade flat da OP,
                                                // quando refugo em operações posteriores reduziu o total de peças
                                                // boas esperado (mesma regra/mesmo texto já usado na tela do
                                                // operador, "Reaberta"). Precisa ser calculada ANTES do status
                                                // (abaixo) — "Produzido Parcial"/"Concluída" comparam contra ela,
                                                // não contra a quantidade flat.
                                                const ehPrimeiraOperacao = opDetalhe.metaAjustadaPrimeiraOperacao?.operacao === operac;
                                                // Meta de verdade desta operação (19/08/2026) — vem do mapa
                                                // completo calculado no backend (calcularMetaOperacao): pra
                                                // 1ª operação, a mesma meta ajustada por refugo posterior de
                                                // sempre; pras demais, o maior entre a quantidade flat e o
                                                // que a operação ANTERIOR já entregou (pode ser mais que o
                                                // planejado, se ela também teve a meta ajustada).
                                                const metaOperacao = opDetalhe.metaPorOperacao?.[operac] ?? opDetalhe.quantidade;
                                                // Duas razões distintas pra meta > quantidade flat, com
                                                // texto de banner próprio cada uma (ver abaixo): refugo em
                                                // operação posterior (só a 1ª operação compensa) vs. a
                                                // operação anterior ter entregue mais peças do que o
                                                // planejado (qualquer operação que não seja a 1ª).
                                                const metaAjustadaPorRefugoPosterior = ehPrimeiraOperacao && metaOperacao > opDetalhe.quantidade;
                                                const metaAjustadaPorEntregaAnterior = !ehPrimeiraOperacao && metaOperacao > opDetalhe.quantidade;
                                                const metaFoiAjustada = metaAjustadaPorRefugoPosterior || metaAjustadaPorEntregaAnterior;
                                                const operacaoAnteriorSeq = indiceOperacao > 0 ? operacoesRoteiroDetalhe[indiceOperacao - 1] : null;
                                                const saldoOperacao = Math.max(metaOperacao - produzidoOperacao, 0);
                                                // "Reaberta" (19/08/2026, pedido do usuário: padronizar com a
                                                // tela do operador, que já tem esse conceito) — só a 1ª operação,
                                                // com meta ajustada, que já produziu o suficiente pra bater a
                                                // quantidade ORIGINAL (por isso já foi/parecia concluída em algum
                                                // momento) mas ainda não bate a meta AJUSTADA (recalculada agora,
                                                // com o refugo mais recente das operações seguintes). Tem
                                                // prioridade sobre "Concluída" (é exatamente o que desmente), mas
                                                // NÃO sobre "Em Produção"/"Pausada" — se alguém está mexendo
                                                // nela agora, isso é mais relevante que o aviso de reabertura.
                                                const reaberta = metaAjustadaPorRefugoPosterior
                                                    && produzidoOperacao >= opDetalhe.quantidade
                                                    && produzidoOperacao < metaOperacao;
                                                // "Produzido Parcial"/"Concluída" (14/08/2026, estendido
                                                // 19/08/2026): ZH6_STATUS volta sempre pra AGUARDANDO entre
                                                // ciclos, mesmo com produção real acumulada — sem essa
                                                // distinção, uma operação já com peças feitas aparecia igual a
                                                // uma nunca tocada. Bug real 19/08/2026 (achado do usuário):
                                                // apontamento feito direto no Protheus nativo (fora do Persona, sem
                                                // NENHUMA linha na ZH6) bateu a meta inteira (300/300) e
                                                // continuava rotulado "Produzido Parcial" — a checagem só olhava
                                                // "produzido > 0", nunca se já tinha batido a meta. Agora
                                                // compara contra `metaOperacao` (a mesma que decide o saldo
                                                // logo acima) pra reconhecer conclusão mesmo sem nenhum registro
                                                // de sessão na ZH6.
                                                const statusLabel = (reaberta && statusBase !== 'Em Produção' && statusBase !== 'Pausada')
                                                    ? 'Reaberta'
                                                    : statusBase !== 'Aguardando'
                                                    ? statusBase
                                                    : metaOperacao > 0 && produzidoOperacao >= metaOperacao
                                                        ? 'Concluída'
                                                        : produzidoOperacao > 0
                                                            ? 'Produzido Parcial'
                                                            : 'Aguardando';
                                                // Detalhe ao clicar (17/08/2026, pedido do usuário): quantidade/
                                                // produzido/saldo/refugo/máquina daquela operação específica —
                                                // refugadoPorOperacao e o nome da máquina (_RECURSO_NOME, já
                                                // resolvido pelo backend em /api/protheus/roteiro) só precisam
                                                // ser calculados quando a operação está expandida.
                                                const expandida = operacaoExpandida === operac;
                                                const refugadoOperacao = opDetalhe.refugadoPorOperacao?.[operac] || 0;
                                                // Código antes do nome (19/08/2026, pedido do usuário, em todo
                                                // lugar que a máquina aparece) — G2_RECURSO é o código real,
                                                // _RECURSO_NOME o nome já resolvido pelo backend.
                                                const codigoMaquinaOperacao = (seq.G2_RECURSO || '').trim();
                                                const nomeMaquinaOperacao = (seq._RECURSO_NOME || '').trim();
                                                const maquinaOperacao = codigoMaquinaOperacao && nomeMaquinaOperacao
                                                    ? `${codigoMaquinaOperacao} - ${nomeMaquinaOperacao}`
                                                    : (codigoMaquinaOperacao || nomeMaquinaOperacao || '—');
                                                // Detalha QUAIS operações posteriores contribuíram refugo, pra
                                                // deixar claro de onde vieram as peças a mais que essa operação
                                                // precisa compensar (pedido explícito do usuário: "na operação 3
                                                // foram refugadas 50 peças" — não só o total agregado).
                                                const refugoPorOperacaoPosterior = metaAjustadaPorRefugoPosterior
                                                    ? Object.entries(opDetalhe.refugadoPorOperacao || {})
                                                        .filter(([op, qtd]) => op !== operac && qtd > 0)
                                                        .sort(([a], [b]) => Number(a) - Number(b))
                                                    : [];
                                                // Operador/horário DESSA operação específica (18/08/2026, pedido
                                                // do usuário) — diferente do que foi removido em nível de OP
                                                // (aquele resumia várias operações numa só máquina/operador,
                                                // enganoso; este é escopado a uma única operação, sem ambiguidade
                                                // nenhuma). "Em produção" mostra quem está trabalhando AGORA e
                                                // "Em andamento" no lugar do fim (sessão ainda aberta); qualquer
                                                // outro status mostra "Último operador" + o horário real que a
                                                // sessão fechou (pausa ou apontamento).
                                                const sessaoOperacao = opDetalhe.sessaoPorOperacao?.[operac] || null;
                                                const emAndamento = sessaoOperacao?.status === 'EM PRODUCAO';
                                                return (
                                                    <div key={seq.G2_OPERAC} className="rounded-xl bg-surface-3/30 border border-white/[0.03] overflow-hidden">
                                                        <button
                                                            type="button"
                                                            onClick={() => setOperacaoExpandida(expandida ? null : operac)}
                                                            className="w-full text-left px-3 py-2 hover:bg-white/[0.02] transition-colors"
                                                        >
                                                            <div className="flex items-center justify-between gap-2">
                                                                <span className="text-[12px] font-medium text-anthracite-300 flex items-center gap-1.5 min-w-0">
                                                                    <ChevronDown size={13} className={`shrink-0 text-anthracite-500 transition-transform ${expandida ? '' : '-rotate-90'}`} />
                                                                    <span className="truncate">Operação {seq.G2_OPERAC}{seq.G2_DESCRI ? ` — ${(seq.G2_DESCRI || '').trim()}` : ''}</span>
                                                                </span>
                                                                <span className={`mes-status shrink-0 ${statusBadgeClass[statusLabel] || 'mes-status--muted'}`}>
                                                                    {statusIcon[statusLabel]}{statusLabel}
                                                                </span>
                                                            </div>
                                                            <div className="mt-1.5 h-[3px] rounded-full bg-white/[0.05] overflow-hidden">
                                                                <div
                                                                    className={`h-full rounded-full ${progressoOperacao > 0 ? 'bg-brand-500' : ''}`}
                                                                    style={{ width: `${progressoOperacao}%` }}
                                                                />
                                                            </div>
                                                        </button>
                                                        {expandida && (
                                                            <div className="px-3 pb-3 pt-1 space-y-2">
                                                                {metaAjustadaPorRefugoPosterior && (
                                                                    <div className="rounded-xl px-3 py-2 text-[11px] font-medium bg-roxo-500/10 border border-roxo-500/15 text-roxo-400">
                                                                        Meta desta operação foi ajustada para <strong>{metaOperacao.toLocaleString('pt-BR')}</strong> peça(s)
                                                                        (planejado inicialmente: {opDetalhe.quantidade.toLocaleString('pt-BR')}), pois refugo em operaç{refugoPorOperacaoPosterior.length > 1 ? 'ões' : 'ão'} seguinte{refugoPorOperacaoPosterior.length > 1 ? 's' : ''} do roteiro
                                                                        {refugoPorOperacaoPosterior.length > 0 && (
                                                                            <> ({refugoPorOperacaoPosterior.map(([op, qtd]) => `Operação ${op}: ${qtd.toLocaleString('pt-BR')} pç(s)`).join(', ')})</>
                                                                        )} reduziu o total de peças boas esperado no final; por ser a 1ª operação do roteiro, ela é quem compensa essa perda.
                                                                    </div>
                                                                )}
                                                                {metaAjustadaPorEntregaAnterior && (
                                                                    <div className="rounded-xl px-3 py-2 text-[11px] font-medium bg-roxo-500/10 border border-roxo-500/15 text-roxo-400">
                                                                        Meta desta operação foi ajustada para <strong>{metaOperacao.toLocaleString('pt-BR')}</strong> peça(s)
                                                                        (planejado inicialmente: {opDetalhe.quantidade.toLocaleString('pt-BR')}), pois a operação {operacaoAnteriorSeq?.G2_OPERAC} entregou mais peças do que o planejado — o saldo aqui reflete o que realmente dá pra produzir.
                                                                    </div>
                                                                )}
                                                                {/* Máquina numa linha própria, largura cheia (17/08/2026,
                                                                    achado do usuário: nome comprido tipo "ALMOXARIFADO
                                                                    PROD. ACAB." não cabia numa coluna do grid de 5,
                                                                    truncava sem dar pra ler). Os 4 números continuam
                                                                    em grid, já que são curtos e ganham com o alinhamento. */}
                                                                <div className="mes-card-sunken px-3 py-2 flex items-center gap-2 min-w-0" title={maquinaOperacao}>
                                                                    <Activity size={13} className="text-anthracite-300 shrink-0" />
                                                                    <span className="text-[9px] font-semibold text-anthracite-500 uppercase tracking-wide shrink-0">Máquina</span>
                                                                    <span className="text-[12px] font-bold text-anthracite-200 truncate">{maquinaOperacao}</span>
                                                                </div>
                                                                {sessaoOperacao && (
                                                                    <>
                                                                        <div className="mes-card-sunken px-3 py-2 flex items-center gap-2 min-w-0" title={sessaoOperacao.operadorNome || sessaoOperacao.operador}>
                                                                            <User size={13} className="text-anthracite-300 shrink-0" />
                                                                            <span className="text-[9px] font-semibold text-anthracite-500 uppercase tracking-wide shrink-0">
                                                                                {emAndamento ? 'Operador' : 'Último operador'}
                                                                            </span>
                                                                            <span className="text-[12px] font-bold text-anthracite-200 truncate">{sessaoOperacao.operadorNome || sessaoOperacao.operador || 'Não preenchido'}</span>
                                                                        </div>
                                                                        <div className="mes-card-sunken px-3 py-2 flex items-center gap-2 min-w-0">
                                                                            <Clock size={13} className="text-anthracite-300 shrink-0" />
                                                                            <span className="text-[9px] font-semibold text-anthracite-500 uppercase tracking-wide shrink-0">Início — Fim</span>
                                                                            <span className="text-[12px] font-bold text-anthracite-200 truncate">
                                                                                {formatDate(sessaoOperacao.inicio)} — {emAndamento ? (
                                                                                    <span className="text-brand-400">Em andamento</span>
                                                                                ) : formatDate(sessaoOperacao.fim)}
                                                                            </span>
                                                                        </div>
                                                                    </>
                                                                )}
                                                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                                                    {[
                                                                        { label: metaFoiAjustada ? 'Meta ajustada' : 'Quantidade', value: metaOperacao, icon: Target, color: metaFoiAjustada ? 'text-roxo-400' : 'text-anthracite-300' },
                                                                        { label: 'Produzido', value: produzidoOperacao, icon: CheckCircle, color: 'text-petrol-400' },
                                                                        { label: 'Saldo', value: saldoOperacao, icon: Layers, color: 'text-brand-400' },
                                                                        { label: 'Refugo', value: refugadoOperacao, icon: AlertTriangle, color: 'text-red-400' },
                                                                    ].map((item, i) => (
                                                                        <div key={i} className="mes-card-sunken p-2.5 text-center">
                                                                            <item.icon size={13} className={`mx-auto mb-1 ${item.color}`} />
                                                                            <span className="block text-[9px] text-anthracite-500 mb-0.5">{item.label}</span>
                                                                            <span className={`text-[13px] font-bold ${item.color} leading-tight block truncate tabular-nums`}>
                                                                                {item.value.toLocaleString('pt-BR')}
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}
                            <div className={`grid gap-5 pt-2 ${opDetalhe.dataEncerramento ? 'grid-cols-3' : 'grid-cols-2'}`}>
                                {[
                                    { label: 'Início', value: formatDate(opDetalhe.data_inicio), icon: Calendar },
                                    { label: 'Fim Previsto', value: formatDate(opDetalhe.previsao_fim), icon: Calendar },
                                    ...(opDetalhe.dataEncerramento
                                        ? [{ label: 'Encerramento', value: formatDate(opDetalhe.dataEncerramento), icon: Lock }]
                                        : []),
                                ].map((item, i) => (
                                    <div key={i}>
                                        <span className="block text-[10px] text-anthracite-500 mb-1 flex items-center gap-1"><item.icon size={10} /> {item.label}</span>
                                        <span className="text-[13px] font-semibold text-anthracite-200">{item.value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="px-7 py-4 border-t border-white/[0.04] flex justify-end shrink-0">
                            <button onClick={() => setOpDetalhe(null)} className="mes-btn-ghost">Fechar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrdensProducao;
